import type {
  PaymentSurfaceReadiness,
  SalesPointPaymentRailsMobile,
} from 'pi-kiosk-shared';
import { isMethodPayableForCount } from 'pi-kiosk-shared';
import { resolveProviderIconAssetId } from 'pi-kiosk-shared/ui';
import i18n from '../../i18n.js';
import { useCustomerFeatureFlags } from '../../features/flags/useCustomerFeatureFlags.js';
import {
  resolveOrderedPaymentMethods,
  type CustomerPaymentMethodId,
} from '../shop/presenters/shopCheckoutPresenter.js';

/** Bank-transfer QR rail on customer PWA (FIO monitoring). */
export const CUSTOMER_BANK_TRANSFER_PROVIDER_ID = 'fio' as const;

/** Card/gateway handoff rail (ThePay redirect). */
export const CUSTOMER_GATEWAY_PROVIDER_ID = 'thepay' as const;

/** Public URL prefix for provider SVG assets served from the customer PWA origin. */
export const CUSTOMER_PROVIDER_ICON_ASSET_BASE_PATH = '/providers/' as const;

export type CustomerPaymentRail = 'bankTransfer' | 'gateway';

export function resolveCustomerGatewayProviderId(providerSlotId?: string | null): string {
  const slotId = providerSlotId?.trim() ?? '';
  if (slotId.length > 0) {
    return slotId;
  }
  return CUSTOMER_GATEWAY_PROVIDER_ID;
}

export function resolveCustomerPaymentProviderId(
  rail: CustomerPaymentRail,
  gatewayProviderSlotId?: string | null
): string {
  return rail === 'bankTransfer'
    ? CUSTOMER_BANK_TRANSFER_PROVIDER_ID
    : resolveCustomerGatewayProviderId(gatewayProviderSlotId);
}

export function customerPaymentProviderTitle(providerId: string): string {
  const assetId = resolveProviderIconAssetId(providerId);
  if (assetId === 'fio') {
    return i18n.t('checkout.providers.fio');
  }
  if (assetId === 'thepay') {
    return i18n.t('checkout.providers.thepay');
  }
  if (assetId === 'stripe') {
    return i18n.t('checkout.providers.stripe');
  }
  return providerId;
}

/**
 * Gateway rail availability from public sales-point config (`paymentRailsMobile.gateway`).
 * Replaces `/health/payment-providers` polling (ADR-HEALTH-SURFACE).
 */
export function useGatewayPaymentAvailable(
  paymentRailsMobile: SalesPointPaymentRailsMobile | null | undefined
): boolean {
  return paymentRailsMobile?.gateway === true;
}

/** Appendix E — v1 ships QR + card; wallet rows gated by `customer_wallet_payments_v1_1`. */
export function useWalletPaymentsFeatureEnabled(): boolean {
  const { flags } = useCustomerFeatureFlags();
  return flags.customerWalletPaymentsV1_1;
}

/**
 * Offer only rails that are policy-allowed and (when readiness is present) payable.
 * CARD additionally requires a live gateway-methods probe (`gatewayMethodsPayable`).
 * While the probe is pending (`null`), CARD is withheld — never offer until checked.
 * Customer always requireVerified; null readiness → fail-closed (no soft offer).
 */
export function resolveVisibleCustomerPaymentMethods(input: {
  readonly bankQr: boolean;
  readonly gateway: boolean;
  readonly walletPaymentsEnabled: boolean;
  readonly paymentSurfaceReadiness?: PaymentSurfaceReadiness | null;
  readonly runtimeRequireVerified?: boolean;
  /** `null` = probe not confirmed yet; `false` = failed; `true` = methods ready. */
  readonly gatewayMethodsPayable?: boolean | null;
  /** HSS v1.3 FR-16a — hide CASH for guests on CUSTOMER_FACING. */
  readonly salesPointInteractionMode?: 'CUSTOMER_FACING' | 'STAFF_OPERATED' | null;
  readonly customerAuthenticated?: boolean;
  readonly checkoutAmountMinor?: number;
  readonly customerAccountDailyCashRemainingMinor?: number | null;
  readonly customerAccountDailyCashRemainingResolved?: boolean;
}): readonly CustomerPaymentMethodId[] {
  // Customer listing+checkout always requireVerified (ignore env / runtime false).
  const requireVerified = true;
  void input.runtimeRequireVerified;
  const readiness = input.paymentSurfaceReadiness;

  let bankQr = input.bankQr;
  let gateway = input.gateway;
  let walletPaymentsEnabled = input.walletPaymentsEnabled;
  let cash = false;

  if (readiness === null || readiness === undefined) {
    // Fail-closed: without readiness, never offer electronic or cash from soft flags alone.
    return resolveOrderedPaymentMethods({
      bankQr: false,
      gateway: false,
      walletPaymentsEnabled: false,
      cash: false,
    });
  }

  bankQr =
    bankQr && isMethodPayableForCount(readiness.methods.bankTransfer, requireVerified);
  gateway =
    gateway && isMethodPayableForCount(readiness.methods.gateway, requireVerified);
  if (walletPaymentsEnabled && readiness.methods.wallet !== undefined) {
    walletPaymentsEnabled = isMethodPayableForCount(
      readiness.methods.wallet,
      requireVerified,
    );
  }
  // CASH Stage 1–4 is offered when readiness says payable (incl. Self-Scan).
  // Customer never self-confirms — staff ConfirmCashPaymentReceivedUseCase completes.
  if (readiness.methods.cash !== undefined) {
    cash = isMethodPayableForCount(readiness.methods.cash, requireVerified);
  }
  if (
    cash &&
    input.salesPointInteractionMode === 'CUSTOMER_FACING' &&
    input.customerAuthenticated !== true
  ) {
    cash = false;
  }
  if (
    cash &&
    input.salesPointInteractionMode !== 'STAFF_OPERATED' &&
    input.customerAuthenticated === true
  ) {
    const remainingResolved = input.customerAccountDailyCashRemainingResolved === true;
    const remainingMinor = input.customerAccountDailyCashRemainingMinor;
    if (!remainingResolved) {
      cash = false;
    } else if (remainingMinor != null && (input.checkoutAmountMinor ?? 0) > remainingMinor) {
      cash = false;
    }
  }

  if (gateway) {
    if (input.gatewayMethodsPayable !== true) {
      gateway = false;
    }
  }

  return resolveOrderedPaymentMethods({
    bankQr,
    gateway,
    walletPaymentsEnabled,
    cash,
  });
}

export interface CustomerAccountDailyCashRemainingDTO {
  readonly customerAccountDailyCashRemainingMinor: number | null;
}

export async function fetchCustomerAccountDailyCashRemaining(input: {
  readonly api: { request: <T>(path: string) => Promise<T> };
}): Promise<CustomerAccountDailyCashRemainingDTO> {
  return input.api.request<CustomerAccountDailyCashRemainingDTO>(
    '/customer-checkout/daily-cash-remaining',
  );
}
